import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  CloudUpload,
  Description,
  Image,
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { UploadedFile, DocumentUploadProps } from './types';
import { FilePreview } from './FilePreview';

export const DocumentUpload: React.FC<DocumentUploadProps> = ({
  config,
  value = [],
  onChange,
  onSubmit,
  disabled = false,
  className,
  style,
}) => {
  const [files, setFiles] = useState<UploadedFile[]>(value);
  const [errors, setErrors] = useState<string[]>([]);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [fileValidationErrors, setFileValidationErrors] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Update internal state when value prop changes
  useEffect(() => {
    setFiles(value);
  }, [value]);

  // Basic validation
  const validateFile = useCallback((file: File): string | null => {
    // File type validation
    const isValidType = config.fileTypes.some(type => {
      if (type.includes('*')) {
        const baseType = type.split('/')[0];
        return file.type.startsWith(baseType);
      }
      return file.type === type;
    });

    if (!isValidType) {
      return `File type ${file.type} is not allowed. Allowed types: ${config.fileTypes.join(', ')}`;
    }

    // File size validation
    if (config.maxSize && file.size > config.maxSize) {
      const maxSizeMB = (config.maxSize / (1024 * 1024)).toFixed(1);
      return `File size exceeds maximum limit of ${maxSizeMB}MB`;
    }

    return null;
  }, [config]);

  const handleFiles = useCallback(async (acceptedFiles: File[]) => {
    console.log('handleFiles called with:', acceptedFiles.length, 'files');
    setHasInteracted(true);
    const newFiles: UploadedFile[] = [];
    const validationErrors: string[] = [];
    
    try {
      for (const file of acceptedFiles) {
        let error: string | null = null;
        
        // Check for duplicate files (when multiple is enabled)
        if (config.multiple) {
          const isDuplicate = files.some(existingFile => 
            existingFile.file.name === file.name && 
            existingFile.file.size === file.size &&
            existingFile.file.lastModified === file.lastModified
          );
          
          if (isDuplicate) {
            validationErrors.push(`${file.name}: File already exists`);
            continue; // Skip this file and process the next one
          }
        }
        
        // Basic validation first
        error = validateFile(file);
        
        // Custom validation (can be async)
        if (!error && config.customValidation) {
          try {
            const customResult = config.customValidation(file);
            if (customResult instanceof Promise) {
              error = await customResult;
            } else {
              error = customResult;
            }
          } catch (customError) {
            error = `Custom validation failed: ${customError instanceof Error ? customError.message : 'Unknown error'}`;
          }
        }
        
        if (error) {
          // Add validation error but don't add file to selected files
          validationErrors.push(`${file.name}: ${error}`);
        } else {
          // Only add valid files to the selected files list
          const newFile: UploadedFile = {
            id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            file,
            preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined,
            progress: 0,
            status: 'pending',
            uploadedAt: new Date(),
          };
          newFiles.push(newFile);
        }
      }

      // Update files list with only valid files
      const updatedFiles = config.multiple ? [...files, ...newFiles] : newFiles;
      setFiles(updatedFiles);
      onChange?.(updatedFiles);

      // Update error state
      if (validationErrors.length > 0) {
        setFileValidationErrors(validationErrors);
        setErrors(validationErrors);
      } else {
        // Clear file validation errors if all files are valid
        setFileValidationErrors([]);
        setErrors([]);
      }
    } catch (error) {
      // Handle any unexpected errors
      const errorMessage = `Unexpected error: ${error instanceof Error ? error.message : 'Unknown error'}`;
      setFileValidationErrors([errorMessage]);
      setErrors([errorMessage]);
    }
  }, [files, config.multiple, validateFile, config.customValidation, onChange]);

  const removeFile = useCallback((id: string) => {
    setHasInteracted(true);
    const updatedFiles = files.filter(f => f.id !== id);
    setFiles(updatedFiles);
    onChange?.(updatedFiles);
    
    // Clear file validation errors when files are removed
    setFileValidationErrors([]);
    
    // Reset the file input to ensure it works properly
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [files, onChange]);

  const handleSubmit = useCallback(() => {
    // Check if required field validation fails
    if (config.required && files.length === 0) {
      // Show required field error only when trying to submit
      setErrors([`${config.title} is required`]);
      return;
    }
    
    // Clear file validation errors when submitting
    setFileValidationErrors([]);
    setHasAttemptedSubmit(true);
    const validFiles = files.filter(f => f.status !== 'error');
    onSubmit?.(validFiles);
  }, [files, onSubmit, config.required, config.title]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleFiles,
    multiple: config.multiple,
    disabled,
    noClick: false,
    noKeyboard: false,
  });

  const getFileTypeIcon = (fileType: string) => {
    if (fileType.startsWith('image/')) return <Image />;
    if (fileType.includes('pdf')) return <Description />;
    return <Description />;
  };

  const allowedTypesText = config.fileTypes
    .map(type => type.replace('*', 'all'))
    .join(', ');

  // Validation errors - only show after user interaction
  useEffect(() => {
    if (!hasInteracted) return;

    const validationErrors: string[] = [];
    
    // Don't show required field errors here - only show actual file validation errors
    // Required field validation will be handled by the submit button being disabled
    
    // Combine file validation errors with any other validation errors
    const allErrors = [...fileValidationErrors, ...validationErrors];
    
    // Update errors based on current state
    if (allErrors.length > 0) {
      setErrors(allErrors);
    } else {
      // Only clear errors if there are no file validation errors and no other validation errors
      setErrors([]);
    }
  }, [files, config.required, config.title, hasInteracted, hasAttemptedSubmit, fileValidationErrors]);

  const getButtonPosition = () => {
    switch (config.submitButton?.position || 'right') {
      case 'left':
        return 'flex-start';
      case 'center':
        return 'center';
      case 'right':
        return 'flex-end';
      default:
        return 'flex-end';
    }
  };

  return (
    <Box className={className} style={style}>
      <Typography variant="h6" gutterBottom>
        {config.title}
        {config.required && <span style={{ color: 'red' }}> *</span>}
      </Typography>
      
      {config.description && (
        <Typography variant="body2" color="textSecondary" gutterBottom>
          {config.description}
        </Typography>
      )}

      <Paper
        {...getRootProps()}
        key={`dropzone-${files.length}`}
        sx={{
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.main' : 'grey.300',
          backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
          p: 3,
          textAlign: 'center',
          cursor: disabled ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': {
            borderColor: disabled ? 'grey.300' : 'primary.main',
            backgroundColor: disabled ? 'background.paper' : 'action.hover',
          },
        }}
      >
        <input {...getInputProps()} ref={fileInputRef} />
        
        <CloudUpload sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
        
        <Typography variant="h6" gutterBottom>
          {isDragActive ? 'Drop files here' : 'Drag & drop files here'}
        </Typography>
        
        <Typography variant="body2" color="textSecondary" gutterBottom>
          or <Button
            variant="text"
            disabled={disabled}
            onClick={e => {
              e.stopPropagation();
              fileInputRef.current?.click();
            }}
          >
            browse files
          </Button>
        </Typography>
        
        <Box sx={{ mt: 2 }}>
          <Chip
            icon={getFileTypeIcon(config.fileTypes[0])}
            label={`Accepted: ${allowedTypesText}`}
            variant="outlined"
            size="small"
          />
          {config.maxSize && (
            <Chip
              label={`Max size: ${(config.maxSize / (1024 * 1024)).toFixed(1)}MB`}
              variant="outlined"
              size="small"
              sx={{ ml: 1 }}
            />
          )}
        </Box>
      </Paper>

      {errors.length > 0 && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {errors.map((error, index) => (
            <Typography key={index} variant="body2">
              {error}
            </Typography>
          ))}
        </Alert>
      )}

      {files.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Selected Files ({files.length})
          </Typography>
          {files.map((file) => (
            <FilePreview
              key={file.id}
              file={file}
              onRemove={removeFile}
            />
          ))}
        </Box>
      )}

      {config.helperText && (
        <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
          {config.helperText}
        </Typography>
      )}

      {/* Submit Button */}
      {config.submitButton && (
        <Box sx={{ 
          mt: 3, 
          display: 'flex', 
          justifyContent: getButtonPosition() 
        }}>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={
              disabled || 
              config.submitButton.disabled || 
              files.length === 0
            }
            startIcon={
              config.submitButton.loading ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
          >
            {config.submitButton.text}
          </Button>
        </Box>
      )}
    </Box>
  );
}; 